import { cl, globs } from "../../../components/utils/utils";

export const DAY_MINS = 1440;
export const CONFLICT_RED = "#e53935";
export const NO_RECIPE_GRAY = "#9aa5b1";
export const CONFLICT_ROW_BG = "#ffebee";
export const MAX_EVENTS_PER_VALVE = 6;

export function ellipsize(str, max = 16) {
  const s = String(str == null ? "" : str);
  if (s.length <= max) return s;
  return `${s.slice(0, Math.max(0, max - 1))}…`;
}

export function parseValve(valve) {
  if (!valve) return { zone: null, channel: null };
  const parts = String(valve).split("-");
  if (parts.length < 2) return { zone: null, channel: null };
  const zone = Number(parts[0]);
  const channel = Number(parts[1]);
  return {
    zone: Number.isFinite(zone) ? zone : null,
    channel: Number.isFinite(channel) ? channel : null,
  };
}

export function channelNameFromChannels(channels, key) {
  const ch = channels && channels[key];
  if (!ch || typeof ch !== "object") return "";
  for (const v of Object.values(ch)) {
    if (typeof v === "string") return v;
  }
  return "";
}

export function zoneNameOf(zones, siteZoneIndex) {
  const z = (zones || []).find((x) => x.siteZoneIndex === siteZoneIndex);
  return z && z.zoneName != null ? z.zoneName : "";
}

/** Display channel as "(chIndex+1) name" */
export function channelLabel(channels, valve) {
  const { channel } = parseValve(valve);
  if (channel == null) return "";
  const name = channelNameFromChannels(channels, valve);
  return `${channel + 1} ${name}`.trim();
}

export function recipeNameOf(recipes, recipeId) {
  if (!recipeId) return "No Recipe";
  const r = (recipes || []).find((x) => x.recipeId === recipeId);
  return r && r.name ? r.name : "No Recipe";
}

export function recipeColorOf(recipes, recipeId) {
  if (!recipeId) return null;
  const r = (recipes || []).find((x) => x.recipeId === recipeId);
  return r && r.color ? r.color : null;
}

export function eventStart(e) {
  return Number(e && e.dayMins) || 0;
}

export function eventEnd(e) {
  return eventStart(e) + Math.max(0, Number(e && e.durMins) || 0);
}

/** Mix hex color with white; whiteFrac 0.5 → 50% white. */
export function mixColorWithWhite(hex, whiteFrac = 0.5) {
  if (!hex || typeof hex !== "string") return NO_RECIPE_GRAY;
  let h = hex.replace("#", "").trim();
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (h.length !== 6 || Number.isNaN(parseInt(h, 16))) return hex;
  const t = Math.max(0, Math.min(1, whiteFrac));
  const mix = (c) => Math.round(c * (1 - t) + 255 * t);
  const r = mix(parseInt(h.slice(0, 2), 16));
  const g = mix(parseInt(h.slice(2, 4), 16));
  const b = mix(parseInt(h.slice(4, 6), 16));
  return `#${[r, g, b]
    .map((x) => x.toString(16).padStart(2, "0"))
    .join("")}`;
}

export function isZoneSequential(zones, siteZoneIndex) {
  const z = (zones || []).find((x) => x.siteZoneIndex === siteZoneIndex);
  return !!(z && z.sequential);
}

/**
 * Compute scheduled vs actual open times per entry.
 * Sequential zones: overlapping/queued valves run in channel-index order;
 * actual open is deferred until the zone is free.
 * Returns array aligned with entries:
 *   { schedStart, schedEnd, openStart, openEnd }
 */
export function computeActualRunTimes(entries, zones) {
  const list = entries || [];
  const result = list.map((e, index) => {
    const schedStart = eventStart(e);
    const dur = Math.max(0, Number(e && e.durMins) || 0);
    const schedEnd = schedStart + dur;
    return {
      index,
      schedStart,
      schedEnd,
      openStart: schedStart,
      openEnd: schedEnd,
    };
  });

  const byZone = new Map();
  result.forEach((r) => {
    const e = list[r.index];
    if (!e || !e.valve) return;
    const { zone, channel } = parseValve(e.valve);
    if (zone == null || !isZoneSequential(zones, zone)) return;
    if (!byZone.has(zone)) byZone.set(zone, []);
    byZone.get(zone).push({
      ...r,
      channel: channel == null ? 0 : channel,
      dur: Math.max(0, r.schedEnd - r.schedStart),
    });
  });

  byZone.forEach((jobs) => {
    const runnable = jobs.filter((j) => j.dur > 0);
    runnable.sort((a, b) => {
      if (a.schedStart !== b.schedStart) return a.schedStart - b.schedStart;
      return a.channel - b.channel;
    });
    let freeAt = 0;
    runnable.forEach((j) => {
      const openStart = Math.max(j.schedStart, freeAt);
      const openEnd = openStart + j.dur;
      freeAt = openEnd;
      result[j.index].openStart = openStart;
      result[j.index].openEnd = openEnd;
    });
  });

  return result;
}

/** Count entries assigned to a valve key (e.g. "5-1"). */
export function countValveEvents(entries, valve) {
  if (!valve) return 0;
  return (entries || []).filter((e) => e && e.valve === valve).length;
}

export function valveAtEventLimit(entries, valve) {
  return countValveEvents(entries, valve) >= MAX_EVENTS_PER_VALVE;
}

/** Half-open [start, end) overlap — any shared minute. */
export function eventsOverlap(a, b) {
  const a0 = eventStart(a);
  const a1 = eventEnd(a);
  const b0 = eventStart(b);
  const b1 = eventEnd(b);
  if (a1 <= a0 || b1 <= b0) return false;
  return a0 < b1 && b0 < a1;
}

/**
 * Conflict = overlapping minutes with two different non-empty recipes.
 * Returns { indices: Set<number>, pair: [i,j]|null }
 */
export function findConflicts(entries) {
  const list = entries || [];
  const indices = new Set();
  let pair = null;
  for (let i = 0; i < list.length; i++) {
    const a = list[i];
    const ra = a && a.recipe;
    if (!ra) continue;
    for (let j = i + 1; j < list.length; j++) {
      const b = list[j];
      const rb = b && b.recipe;
      if (!rb || ra === rb) continue;
      if (!eventsOverlap(a, b)) continue;
      indices.add(i);
      indices.add(j);
      if (!pair) pair = [i, j];
    }
  }
  return { indices, pair };
}

export function formatConflictLine(entries, pair, zones, channels) {
  if (!pair) return null;
  const [i, j] = pair;
  const a = entries[i];
  const b = entries[j];
  const fmt = (e) => {
    const zn = zoneNameOf(zones, parseValve(e.valve).zone) || "?";
    const ch = channelLabel(channels, e.valve) || "?";
    const n =
      ((Math.round(eventStart(e)) % DAY_MINS) + DAY_MINS) % DAY_MINS;
    const h24 = Math.floor(n / 60);
    const m = n % 60;
    const ampm = h24 >= 12 ? "PM" : "AM";
    const h12 = h24 % 12 || 12;
    return `${zn}-${ch} @ ${h12}:${String(m).padStart(2, "0")}${ampm}`;
  };
  return `Conflict: ${fmt(a)}, ${fmt(b)}.`;
}

/** Group valves by zone (alpha by name), channels by index. */
export function buildValveRows(zones, valves, channels) {
  const byZone = new Map();
  (valves || []).forEach((v) => {
    const { zone, channel } = parseValve(v);
    if (zone == null) return;
    if (!byZone.has(zone)) byZone.set(zone, []);
    byZone.get(zone).push({ valve: v, channel });
  });

  const zoneList = [...byZone.keys()].map((siteZoneIndex) => ({
    siteZoneIndex,
    zoneName: zoneNameOf(zones, siteZoneIndex) || `Zone ${siteZoneIndex}`,
    channels: byZone
      .get(siteZoneIndex)
      .slice()
      .sort((a, b) => a.channel - b.channel),
  }));
  zoneList.sort((a, b) =>
    String(a.zoneName).localeCompare(String(b.zoneName))
  );

  const rows = [];
  zoneList.forEach((z) => {
    rows.push({
      type: "zone",
      key: `z-${z.siteZoneIndex}`,
      siteZoneIndex: z.siteZoneIndex,
      zoneName: z.zoneName,
    });
    z.channels.forEach((ch) => {
      rows.push({
        type: "valve",
        key: ch.valve,
        valve: ch.valve,
        siteZoneIndex: z.siteZoneIndex,
        zoneName: z.zoneName,
        label: channelLabel(channels, ch.valve),
        channel: ch.channel,
      });
    });
  });
  return rows;
}

/**
 * Initial view window in day minutes.
 * No events → 6am–12pm.
 * Else → 1h before first start to 1h after last end; expand to 6h centered if shorter.
 */
export function computeInitialWindow(entries) {
  const list = (entries || []).filter((e) => e && e.valve);
  if (!list.length) {
    return { viewStart: 6 * 60, viewEnd: 12 * 60 };
  }
  let minStart = Infinity;
  let maxEnd = -Infinity;
  list.forEach((e) => {
    minStart = Math.min(minStart, eventStart(e));
    maxEnd = Math.max(maxEnd, eventEnd(e));
  });
  if (!Number.isFinite(minStart)) {
    return { viewStart: 6 * 60, viewEnd: 12 * 60 };
  }
  let viewStart = minStart - 60;
  let viewEnd = maxEnd + 60;
  if (viewEnd - viewStart < 6 * 60) {
    const mid = (minStart + maxEnd) / 2;
    viewStart = mid - 3 * 60;
    viewEnd = mid + 3 * 60;
  }
  if (viewEnd - viewStart > DAY_MINS) {
    viewStart = 0;
    viewEnd = DAY_MINS;
  }
  if (viewStart < 0) {
    viewEnd -= viewStart;
    viewStart = 0;
  }
  if (viewEnd > DAY_MINS) {
    viewStart -= viewEnd - DAY_MINS;
    viewEnd = DAY_MINS;
    if (viewStart < 0) viewStart = 0;
  }
  return { viewStart, viewEnd };
}

export function clampView(viewStart, viewEnd) {
  let span = viewEnd - viewStart;
  span = Math.max(60, Math.min(DAY_MINS, span));
  let start = viewStart;
  let end = start + span;
  if (start < 0) {
    start = 0;
    end = span;
  }
  if (end > DAY_MINS) {
    end = DAY_MINS;
    start = end - span;
  }
  if (start < 0) start = 0;
  return { viewStart: start, viewEnd: end };
}

export function formatShortTime(mins) {
  const n = ((Math.round(Number(mins) || 0) % DAY_MINS) + DAY_MINS) % DAY_MINS;
  const h24 = Math.floor(n / 60);
  const m = n % 60;
  const h12 = h24 % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")}`;
}

export function formatHourTick(mins) {
  const n = ((Math.round(mins) % DAY_MINS) + DAY_MINS) % DAY_MINS;
  const h24 = Math.floor(n / 60);
  if (h24 === 0) return "12AM";
  if (h24 === 12) return "12PM";
  if (h24 < 12) return `${h24}AM`;
  return `${h24 - 12}PM`;
}

/** Snap to nearest :05 if within snapPx of that boundary. */
export function snapDayMins(dayMins, minsPerPx, snapPx = 5) {
  const nearest = Math.round(dayMins / 5) * 5;
  const distPx = Math.abs(dayMins - nearest) / Math.max(minsPerPx, 1e-6);
  if (distPx <= snapPx) return nearest;
  return dayMins;
}

/**
 * For each minute-range segment across the view, determine current-recipe display:
 * null | { type:'recipe', recipeId, color } | { type:'conflict' }
 */
export function buildCurrentRecipeSegments(entries, recipes, viewStart, viewEnd) {
//   cl(entries)
  const list = (entries || []).filter(
    (e) => e && e.valve && eventEnd(e) > eventStart(e)
  );
  const points = new Set([viewStart, viewEnd]);
  list.forEach((e) => {
    const s = Math.max(viewStart, eventStart(e));
    const en = Math.min(viewEnd, eventEnd(e));
    if (en > s) {
      points.add(s);
      points.add(en);
    }
  });
  const sorted = [...points].sort((a, b) => a - b);
  const segs = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const t0 = sorted[i];
    const t1 = sorted[i + 1];
    if (t1 <= t0) continue;
    const mid = (t0 + t1) / 2;
    const active = list.filter(
      (e) => eventStart(e) <= mid && mid < eventEnd(e) && e.recipe
    );
    if (!active.length) {
      segs.push({ start: t0, end: t1, type: "empty" });
      continue;
    }
    const recipesSet = new Set(active.map((e) => e.recipe));
    if (recipesSet.size > 1) {
      segs.push({ start: t0, end: t1, type: "conflict" });
    } else {
      const recipeId = active[0].recipe;
      segs.push({
        start: t0,
        end: t1,
        type: "recipe",
        recipeId,
        color: recipeColorOf(recipes, recipeId) || NO_RECIPE_GRAY,
      });
    }
  }
  return segs;
}
